import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getPosts } from '../../api/news';

const NewsWidget = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const result = await getPosts({ limit: 5 });
        setNews(result.items);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load recent news');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-4 h-full">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
          Recent News
        </h2>
        <div className="flex justify-center py-4">
          <svg
            className="w-6 h-6 text-blue-600 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-4 h-full">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
          Recent News
        </h2>
        <div className="mb-4 p-3 rounded-md bg-red-100 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow p-4 h-full">
        <h2 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
          Recent News
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          No recent news or announcements.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow flex flex-col h-full">
      {/* Header */}
      <div className="p-4 text-white bg-neutral-800 dark:bg-neutral-900 rounded-t-lg">
        <h2 className="text-lg font-semibold">Recent News</h2>
      </div>

      {/* List */}
      <ul className="flex-1 overflow-auto">
        {news.map((item, index) => (
          <React.Fragment key={item.id}>
            {index > 0 && <hr className="border-gray-300 dark:border-gray-700" />}
            <li className="p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <Link
                to={`/news/${item.id}`}
                className="flex items-start no-underline text-inherit"
              >
                <div className="mr-4 flex-shrink-0">
                  {item.is_pinned ? (
                    <svg
                      className="w-6 h-6 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M4 13V7a4 4 0 018 0v6a4 4 0 01-8 0z" />
                      <path
                        fillRule="evenodd"
                        d="M6 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-6 h-6 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 2a8 8 0 100 16 8 8 0 000-16zM8 10a2 2 0 114 0 2 2 0 01-4 0z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-base font-semibold truncate text-gray-900 dark:text-white">
                    {item.title}
                  </p>
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(item.published_at), 'MMM d, yyyy')}
                  </div>
                  {item.roles.length > 0 && (
                    <div className="mt-1">
                      {item.roles.slice(0, 2).map(role => (
                        <span
                          key={role.id}
                          className="inline-block mr-1 mb-1 px-2 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded"
                        >
                          {role.name}
                        </span>
                      ))}
                      {item.roles.length > 2 && (
                        <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded">
                          +{item.roles.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            </li>
          </React.Fragment>
        ))}
      </ul>

      {/* Footer */}
      <hr className="border-t border-gray-300 dark:border-gray-700" />
      <div className="p-2 text-center">
        <Link to="/news" className="text-sm text-blue-600 hover:underline">
          View All News
        </Link>
      </div>
    </div>
  );
};

export default NewsWidget;